import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import { HeartPulse, LayoutDashboard, LogOut, Menu, Shield } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  if (pathname === "/login" || pathname === "/register") return null;

  const links = [
    { label: "Home", to: "/" },
    { label: "Services", to: "/services" },
    { label: "Cities", to: "/cities" },
    { label: "Providers", to: "/providers" },
    { label: "Map", to: "/map" },
    ...(user?.role === "patient" ? [{ label: "AI Assistant", to: "/assistant" }] : []),
    { label: "About", to: "/about" },
  ];

  const go = (to) => { setOpen(false); navigate(to); };
  const isAdmin = user?.is_admin || user?.role === "admin";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-white/50">
      <div className="flex h-1.5 w-full" data-testid="tricolor-top-bar" aria-hidden="true">
        <span className="flex-1 bg-[#FF9933]" />
        <span className="flex-1 bg-white border-y border-[#E4E7EC]" />
        <span className="flex-1 bg-[#138808]" />
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-[#0D5C75] text-white"><HeartPulse size={20} /></span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-lg text-[#0D5C75] tracking-tight">SarvaSeva</span>
            <span className="hidden sm:block text-[10px] text-[#2A9D8F] font-medium tracking-wide">Complete Service. Complete Care.</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.label} to={l.to}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pathname === l.to ? "text-[#0D5C75] bg-[#E9F5F9]" : "text-[#475467] hover:text-[#0D5C75] hover:bg-[#E9F5F9]"}`}
              data-testid={`nav-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <NotificationBell />
              {isAdmin && (
                <Button variant="outline" className="hidden sm:inline-flex border-[#D97706]/40 text-[#D97706] hover:bg-[#FEF3E2]"
                  onClick={() => navigate("/admin")} data-testid="nav-admin-btn">
                  <Shield size={18} className="mr-1.5" /> Admin
                </Button>
              )}
              <Button variant="outline" className="hidden sm:inline-flex border-[#0D5C75]/30 text-[#0D5C75] hover:bg-[#E9F5F9]"
                onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn">
                <LayoutDashboard size={18} className="mr-1.5" /> Dashboard
              </Button>
              <Button variant="ghost" className="text-muted-foreground hover:text-destructive hidden sm:inline-flex"
                onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn"><LogOut size={18} /></Button>
            </>
          ) : (
            <>
              <Button variant="ghost" className="hidden sm:inline-flex text-[#0D5C75] hover:bg-[#E9F5F9]"
                onClick={() => navigate("/login")} data-testid="nav-login-btn">Sign in</Button>
              <Button className="hidden sm:inline-flex rounded-full bg-[#D97706] text-white hover:bg-[#b45f05] hover:text-white"
                onClick={() => navigate("/register")} data-testid="nav-getstarted-btn">Get started</Button>
            </>
          )}

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden grid place-items-center h-10 w-10 rounded-lg hover:bg-[#E9F5F9]" data-testid="nav-mobile-toggle">
                <Menu size={22} className="text-[#0D5C75]" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-1">
                {links.map((l) => (
                  <button key={l.label} onClick={() => go(l.to)}
                    className="text-left px-3 py-3 rounded-lg text-[#101828] hover:bg-[#E9F5F9] font-medium"
                    data-testid={`nav-mobile-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>{l.label}</button>
                ))}
                <div className="h-px bg-[#E4E7EC] my-3" />
                {user ? (
                  <>
                    <button onClick={() => go("/dashboard")} className="text-left px-3 py-3 rounded-lg text-[#101828] hover:bg-[#E9F5F9] font-medium">Dashboard</button>
                    {isAdmin && <button onClick={() => go("/admin")} className="text-left px-3 py-3 rounded-lg text-[#D97706] hover:bg-[#FEF3E2] font-medium" data-testid="nav-mobile-admin">Admin Console</button>}
                    <button onClick={() => { logout(); go("/"); }} className="text-left px-3 py-3 rounded-lg text-destructive hover:bg-destructive/5 font-medium">Log out</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => go("/login")} className="text-left px-3 py-3 rounded-lg text-[#0D5C75] hover:bg-[#E9F5F9] font-medium">Sign in</button>
                    <Button className="mt-1 rounded-full bg-[#D97706] text-white hover:bg-[#b45f05] hover:text-white" onClick={() => go("/register")}>Get started</Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
